import Network from "../network/network";
import { IAddressParserResult } from "../interfaces/IAddressParserResult";

export default class AddressParserService {
    private readonly network: Network;

    constructor(network: Network) {
        this.network = network;
    }

    /**
     * Extract contact information from plain text
     * @param text The text containing contact information
     * @returns Parsed contact information
     */
    public async extractFromText(text: string): Promise<IAddressParserResult> {
        const url = `${Network.AddressParserURL}/extract`;
        const token = await this.network.authenticate();
        
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "Authorization": this.network.getAuthorizationHeader(token)
            },
            body: JSON.stringify({ text })
        });

        if (!response.ok) {
            throw new Error(`Address parser failed with status ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Extract contact information from an image (business card, etc.)
     * @param file The image file to process
     * @returns Parsed contact information
     */
    public async extractFromImage(file: File | Blob): Promise<IAddressParserResult> {
        const url = `${Network.AddressParserURL}/extract-image`;
        const token = await this.network.authenticate();
        
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "accept": "application/json",
                "Authorization": this.network.getAuthorizationHeader(token)
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`Address parser failed with status ${response.status}`);
        }

        return await response.json();
    }

    /**
     * Create a new contact (person and/or institution)
     * @param contactData The contact data to create
     * @returns Response with created person/institution IDs
     */
    public async createContact(contactData: any): Promise<any> {
        const url = `${Network.AddressParserURL}/createContact`;
        const token = await this.network.authenticate();
        
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "Authorization": this.network.getAuthorizationHeader(token)
            },
            body: JSON.stringify(contactData)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Failed to create contact: ${response.status}`);
        }

        const data = await response.json();
        // Map PascalCase response to camelCase
        return {
            success: data.Success,
            personId: data.PersonId,
            institutionId: data.InstitutionId,
            message: data.Message
        };
    }
}
